import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  Check,
  Plus,
  Apple,
  Cookie,
  Milk,
  Home as HomeIcon,
  Baby,
  Pill,
  Sparkles,
  RotateCcw,
} from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill as TagPill, SectionHeader } from "@/components/dryvn";
import { CollapsibleList } from "@/components/collapsible-list";
// Screen migration (2026-07-05): list via hook (local state layers the
// vendored optimistic UX); recent/suggestions stay fixtures — agent
// suggestions are a Vertex-session feature (PORT_NOTES).
import {
  shoppingRecent,
  shoppingSuggestions,
  type ShoppingCategory,
  type ShoppingItem,
} from "@/data/mock";
import { useShopping, useAddShoppingItem, useSetShoppingDone } from "@/lib/queries";

export const Route = createFileRoute("/more/shopping")({
  head: () => ({
    meta: [
      { title: "Shopping list — Dryvn Life" },
      { name: "description", content: "The family shopping list, grouped by aisle." },
    ],
  }),
  component: ShoppingPage,
});

const CATEGORIES: { name: ShoppingCategory; Icon: typeof Apple }[] = [
  { name: "Produce", Icon: Apple },
  { name: "Pantry", Icon: Cookie },
  { name: "Dairy & bread", Icon: Milk },
  { name: "Household", Icon: HomeIcon },
  { name: "Kids", Icon: Baby },
  { name: "Pharmacy", Icon: Pill },
];

function ShoppingPage() {
  const { data: hookItems } = useShopping();
  const addItem = useAddShoppingItem();
  const setDone = useSetShoppingDone();
  const [items, setItems] = useState<ShoppingItem[]>(hookItems);
  const [input, setInput] = useState("");
  const [category, setCategory] = useState<ShoppingCategory>("Produce");

  // Re-seed the local optimistic list whenever the hook refetches (live);
  // in demo the fixtures never change, so behavior is identical.
  useEffect(() => setItems(hookItems), [hookItems]);

  const add = (name: string, cat: ShoppingCategory = category) => {
    if (!name.trim()) return;
    setItems((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: name.trim(), done: false, category: cat },
    ]);
    addItem.mutate({ name: name.trim(), category: cat });
    setInput("");
  };

  const clearDone = () => {
    const checked = items.filter((x) => x.done);
    if (checked.length === 0) return;
    // Re-mark each checked item purchased. The live GET excludes purchased
    // items, so the invalidated refetch removes them for real — the optimistic
    // hide matches that. If a PUT fails, restore that one item so nothing stays
    // hidden locally while still live on the server (no phantom-clear).
    setItems((prev) => prev.filter((i) => !i.done));
    for (const i of checked) {
      setDone.mutate(
        { id: i.id, done: true },
        {
          onError: () =>
            setItems((prev) => (prev.some((x) => x.id === i.id) ? prev : [...prev, i])),
        },
      );
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<ShoppingCategory, ShoppingItem[]>();
    for (const cat of CATEGORIES) map.set(cat.name, []);
    for (const it of items) map.get(it.category)?.push(it);
    return map;
  }, [items]);

  const remaining = items.filter((i) => !i.done).length;
  const done = items.length - remaining;

  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Household"
        title={
          <>
            Shopping{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              list
            </em>
            .
          </>
        }
      >
        <div
          className="flex flex-wrap items-center gap-2 text-xs"
          style={{ color: "var(--dryvn-primary-deep)" }}
        >
          <TagPill tone="life">{remaining} to grab</TagPill>
          <TagPill tone="muted">{done} in the cart</TagPill>
        </div>
      </HeroCard>

      {/* Quick add */}
      <DCard className="mt-6">
        <div className="mb-3 flex flex-wrap gap-2">
          {CATEGORIES.map(({ name, Icon }) => {
            const active = category === name;
            return (
              <button
                key={name}
                onClick={() => setCategory(name)}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors"
                style={{
                  borderColor: active ? "var(--dryvn-primary)" : "var(--dryvn-line)",
                  backgroundColor: active
                    ? "color-mix(in oklab, var(--dryvn-life) 25%, transparent)"
                    : "transparent",
                  color: active ? "var(--dryvn-primary-deep)" : "var(--dryvn-grey)",
                }}
              >
                <Icon className="h-3 w-3" /> {name}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add(input)}
            placeholder={`Add to ${category}…`}
            className="flex-1 rounded-full border px-4 py-2 text-sm outline-none focus:ring-2"
            style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-ink)" }}
          />
          <button
            onClick={() => add(input)}
            className="grid h-9 w-9 place-items-center rounded-full text-white shadow-glow"
            style={{ backgroundColor: "var(--dryvn-primary)" }}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </DCard>

      {/* Suggestions */}
      <div className="mt-6">
        <SectionHeader
          eyebrow="Suggested"
          title="Regulars"
          action={
            <span
              className="flex items-center gap-1 text-xs"
              style={{ color: "var(--dryvn-grey)" }}
            >
              <Sparkles className="h-3 w-3" /> from your history
            </span>
          }
        />
        <div className="flex flex-wrap gap-2">
          {shoppingSuggestions.map((s) => (
            <button
              key={s}
              onClick={() => add(s, "Pantry")}
              className="rounded-full border border-dashed px-3 py-1 text-xs transition-colors hover:border-solid"
              style={{
                borderColor: "var(--dryvn-line)",
                color: "var(--dryvn-primary-deep)",
                backgroundColor: "color-mix(in oklab, var(--dryvn-lime) 12%, transparent)",
              }}
            >
              + {s}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped list */}
      <div className="mt-8 space-y-6">
        {CATEGORIES.map(({ name, Icon }) => {
          const list = grouped.get(name) ?? [];
          if (list.length === 0) return null;
          const groupDone = list.filter((i) => i.done).length;
          return (
            <section key={name}>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className="grid h-7 w-7 place-items-center rounded-lg"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--dryvn-life) 25%, transparent)",
                  }}
                >
                  <Icon className="h-3.5 w-3.5" style={{ color: "var(--dryvn-primary-deep)" }} />
                </span>
                <h3 className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
                  {name}
                </h3>
                <span className="font-mono text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                  {groupDone}/{list.length}
                </span>
              </div>
              <CollapsibleList items={list} resource={`${name.toLowerCase()} items`}>
                {(shown) => (
                  <DCard className="p-2">
                    <ul>
                      {shown.map((it) => (
                        <li key={it.id}>
                          <button
                            onClick={() => {
                              setItems((prev) =>
                                prev.map((x) => (x.id === it.id ? { ...x, done: !x.done } : x)),
                              );
                              setDone.mutate({ id: it.id, done: !it.done });
                            }}
                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-[var(--dryvn-page-tint)]"
                          >
                            <span
                              className="grid h-5 w-5 place-items-center rounded-md border-2 transition-colors"
                              style={{
                                borderColor: it.done ? "var(--dryvn-primary)" : "var(--dryvn-line)",
                                backgroundColor: it.done ? "var(--dryvn-primary)" : "transparent",
                              }}
                            >
                              {it.done && <Check className="h-3 w-3 text-white" />}
                            </span>
                            <span className="flex-1">
                              <span
                                className="block text-sm"
                                style={{
                                  color: it.done ? "var(--dryvn-grey)" : "var(--dryvn-ink)",
                                  textDecoration: it.done ? "line-through" : "none",
                                }}
                              >
                                {it.name}
                              </span>
                              {it.note && (
                                <span
                                  className="block text-[11px]"
                                  style={{ color: "var(--dryvn-grey)" }}
                                >
                                  {it.note}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          );
        })}
      </div>

      {/* Recent + clear */}
      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        <DCard className="lg:col-span-2">
          <SectionHeader title="Recently bought" eyebrow="History" />
          <ul className="space-y-2">
            {shoppingRecent.map((r) => (
              <li key={r.name} className="flex items-center justify-between text-sm">
                <span style={{ color: "var(--dryvn-ink)" }}>{r.name}</span>
                <span className="font-mono text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                  {r.when}
                </span>
              </li>
            ))}
          </ul>
        </DCard>
        <DCard>
          <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
            Clean up
          </p>
          <p
            className="mt-1 font-display text-2xl leading-tight"
            style={{ color: "var(--dryvn-ink)" }}
          >
            {done} in the cart
          </p>
          <button
            onClick={clearDone}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"
            style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-primary-deep)" }}
          >
            <RotateCcw className="h-3 w-3" /> Clear checked
          </button>
        </DCard>
      </div>
    </AppShell>
  );
}
